/**
 * Admin Reports Component
 * Comprehensive reporting for platform analytics
 * 
 * B2: Reports Navigation Tab
 * - Added tabs for: User Activity, Trading, Revenue, Audit Logs
 */
import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import '../pages/AdminPanel.css';
import { adminExports } from '../utils/comprehensiveExport';

interface PlatformOverview {
  total_brokers: number;
  total_indian_exchanges: number;
  total_crypto_exchanges: number;
  total_indicators: number;
  total_keywords: number;
  total_channels: number;
  total_operators: number;
}

interface ThemeUsage {
  theme_id: string;
  theme_name: string;
  user_count: number;
  percentage: number;
  is_default: boolean;
}

interface LanguageUsage {
  language_code: string;
  language_name: string;
  user_count: number;
  percentage: number;
}

interface FeatureUsage {
  feature_id: string;
  feature_name: string;
  description: string;
  usage_count: number;
  active_users: number;
  avg_session_minutes: number;
}

// B2: New interfaces for new report tabs
interface UserActivity {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  activity_type: string;
  action: string;
  details: string;
  ip_address: string;
  device_type: string;
  status: string;
  created_at: string;
}

interface Trade {
  id: string;
  trade_id: string;
  user_name: string;
  strategy_name: string;
  symbol: string;
  exchange: string;
  side: string;
  quantity: number;
  entry_price: number;
  exit_price: number;
  pnl: number;
  status: string;
  created_at: string;
}

interface Subscription {
  id: string;
  user_name: string;
  user_email: string;
  plan_name: string;
  billing_cycle: string;
  amount: number;
  status: string;
  started_at: string;
  expires_at: string;
}

interface AuditLog {
  id: string;
  user_name: string;
  action: string;
  resource_type: string;
  resource_id: string;
  status: string;
  ip_address: string;
  created_at: string;
}

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const AdminReports: React.FC = () => {
  const [activeReport, setActiveReport] = useState('overview');
  const [platformOverview, setPlatformOverview] = useState<PlatformOverview | null>(null);
  const [themeData, setThemeData] = useState<{ themes: ThemeUsage[]; total_users: number } | null>(null);
  const [languageData, setLanguageData] = useState<{ languages: LanguageUsage[]; total_users: number } | null>(null);
  const [featureData, setFeatureData] = useState<{ features: FeatureUsage[] } | null>(null);
  const [brokerData, setBrokerData] = useState<any>(null);
  const [exchangeData, setExchangeData] = useState<any>(null);
  const [indicatorData, setIndicatorData] = useState<any>(null);
  const [channelData, setChannelData] = useState<any>(null);
  const [operatorData, setOperatorData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // B2: New state for activity, trading, revenue, audit reports
  const [userActivities, setUserActivities] = useState<UserActivity[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [activitySummary, setActivitySummary] = useState<any>(null);
  const [tradingSummary, setTradingSummary] = useState<any>(null);
  const [revenueSummary, setRevenueSummary] = useState<any>(null);
  const [auditSummary, setAuditSummary] = useState<any>(null);

  // B2: Filter states
  const [dateRange, setDateRange] = useState('last_30_days');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAllReports();
  }, []);

  useEffect(() => {
    // B2: Fetch specific report when tab changes
    if (activeReport === 'activity') fetchUserActivity();
    if (activeReport === 'trading') fetchTradingReport();
    if (activeReport === 'revenue') fetchRevenueReport();
    if (activeReport === 'audit') fetchAuditLogs();
  }, [activeReport, dateRange]);

  const fetchAllReports = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` };
      
      const [platformRes, themeRes, langRes, featureRes, brokerRes, exchangeRes, indicatorRes, channelRes, operatorRes] = await Promise.all([
        fetch('/api/admin/reports/platform-overview', { headers }),
        fetch('/api/admin/reports/themes', { headers }),
        fetch('/api/admin/reports/languages', { headers }),
        fetch('/api/admin/reports/features', { headers }),
        fetch('/api/admin/reports/brokers', { headers }),
        fetch('/api/admin/reports/exchanges', { headers }),
        fetch('/api/admin/reports/indicators', { headers }),
        fetch('/api/admin/reports/channels', { headers }),
        fetch('/api/admin/reports/operators', { headers }),
      ]);

      if (platformRes.ok) setPlatformOverview(await platformRes.json());
      if (themeRes.ok) setThemeData(await themeRes.json());
      if (langRes.ok) setLanguageData(await langRes.json());
      if (featureRes.ok) setFeatureData(await featureRes.json());
      if (brokerRes.ok) setBrokerData(await brokerRes.json());
      if (exchangeRes.ok) setExchangeData(await exchangeRes.json());
      if (indicatorRes.ok) setIndicatorData(await indicatorRes.json());
      if (channelRes.ok) setChannelData(await channelRes.json());
      if (operatorRes.ok) setOperatorData(await operatorRes.json());
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // B2: Fetch functions for new reports
  const fetchUserActivity = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` };
      const response = await fetch(`/api/admin/reports/user-activity?date_range=${dateRange}&per_page=50`, { headers });
      if (response.ok) {
        const data = await response.json();
        setUserActivities(data.data || []);
        setActivitySummary(data.summary || null);
      }
    } catch (error) {
      console.error('Failed to fetch user activity:', error);
    }
  };

  const fetchTradingReport = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` };
      const response = await fetch(`/api/admin/reports/trading?date_range=${dateRange}&per_page=50`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTrades(data.data || []);
        setTradingSummary(data.summary || null);
      }
    } catch (error) {
      console.error('Failed to fetch trading report:', error);
    }
  };

  const fetchRevenueReport = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` };
      const response = await fetch(`/api/admin/reports/revenue?date_range=${dateRange}&per_page=50`, { headers });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
        setRevenueSummary(data.summary || null);
      }
    } catch (error) {
      console.error('Failed to fetch revenue report:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` };
      const response = await fetch(`/api/admin/reports/audit-logs?date_range=${dateRange}&per_page=100`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.data || []);
        setAuditSummary(data.summary || null);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  };

  const renderOverview = () => (
    <div className="reports-overview">
      <h3>📊 Platform Overview</h3>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🏦</div>
          <div className="stat-info">
            <span className="stat-value">{platformOverview?.total_brokers || 0}</span>
            <span className="stat-label">Total Brokers</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-info">
            <span className="stat-value">{(platformOverview?.total_indian_exchanges || 0) + (platformOverview?.total_crypto_exchanges || 0)}</span>
            <span className="stat-label">Total Exchanges</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <span className="stat-value">{platformOverview?.total_indicators || 0}</span>
            <span className="stat-label">Indicators</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔤</div>
          <div className="stat-info">
            <span className="stat-value">{platformOverview?.total_keywords || 0}</span>
            <span className="stat-label">Keywords</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📱</div>
          <div className="stat-info">
            <span className="stat-value">{platformOverview?.total_channels || 0}</span>
            <span className="stat-label">Channels</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔣</div>
          <div className="stat-info">
            <span className="stat-value">{platformOverview?.total_operators || 0}</span>
            <span className="stat-label">Operators</span>
          </div>
        </div>
      </div>
      
      <div className="charts-grid">
        <div className="chart-card">
          <h3>🎨 Theme Usage</h3>
          {themeData && (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={themeData.themes}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ theme_name, percentage }) => `${theme_name} ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="user_count"
                    nameKey="theme_name"
                  >
                    {themeData.themes.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <p className="chart-note">Most popular: {themeData.themes[0]?.theme_name} ({themeData.themes[0]?.percentage}%)</p>
            </>
          )}
        </div>

        <div className="chart-card">
          <h3>🌐 Language Usage</h3>
          {languageData && (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={languageData.languages.slice(0, 7)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="language_name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="user_count" fill="#6366f1" />
                </BarChart>
              </ResponsiveContainer>
              <p className="chart-note">Total users: {languageData.total_users}</p>
            </>
          )}
        </div>
      </div>

      <div className="chart-card full-width">
        <h3>🚀 Feature Usage</h3>
        {featureData && (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={featureData.features}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="feature_name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="usage_count" fill="#10b981" name="Usage Count" />
              <Bar dataKey="active_users" fill="#6366f1" name="Active Users" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );

  const renderThemes = () => (
    <div className="report-section">
      <h3>🎨 Theme Usage Report</h3>
      <p className="report-desc">Track which users use which theme (light/dark/custom)</p>
      
      {themeData && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <span className="stat-value">{themeData.total_users}</span>
                <span className="stat-label">Total Users</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-info">
                <span className="stat-value">{themeData.most_popular}</span>
                <span className="stat-label">Most Popular</span>
              </div>
            </div>
          </div>

          <div className="chart-card">
            <h4>Theme Distribution</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={themeData.themes}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ theme_name, percentage }) => `${theme_name}: ${percentage}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="user_count"
                  nameKey="theme_name"
                >
                  {themeData.themes.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <table className="monthly-table">
            <thead>
              <tr>
                <th>Theme</th>
                <th>Users</th>
                <th>Percentage</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {themeData.themes.map((theme) => (
                <tr key={theme.theme_id}>
                  <td>{theme.theme_name}</td>
                  <td>{theme.user_count}</td>
                  <td>{theme.percentage}%</td>
                  <td>
                    {theme.is_default && <span className="badge" style={{ backgroundColor: '#10b981' }}>Default</span>}
                    {!theme.is_default && <span className="badge" style={{ backgroundColor: '#6b7280' }}>Optional</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  const renderLanguages = () => (
    <div className="report-section">
      <h3>🌐 Language Usage Report</h3>
      <p className="report-desc">Track language preference across the platform</p>
      
      {languageData && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <span className="stat-value">{languageData.total_users}</span>
                <span className="stat-label">Total Users</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🌍</div>
              <div className="stat-info">
                <span className="stat-value">{languageData.languages.length}</span>
                <span className="stat-label">Languages</span>
              </div>
            </div>
          </div>

          <div className="chart-card">
            <h4>Language Distribution</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={languageData.languages}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="language_name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="user_count" fill="#6366f1" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <table className="monthly-table">
            <thead>
              <tr>
                <th>Language</th>
                <th>Code</th>
                <th>Users</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {languageData.languages.map((lang) => (
                <tr key={lang.language_code}>
                  <td>{lang.language_name}</td>
                  <td><code>{lang.language_code}</code></td>
                  <td>{lang.user_count}</td>
                  <td>{lang.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  const renderFeatures = () => (
    <div className="report-section">
      <h3>🚀 Feature Usage Report</h3>
      <p className="report-desc">Track usage of AI Copilot, Strategy Builder, and other features</p>
      
      {featureData && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <span className="stat-value">{featureData.features.length}</span>
                <span className="stat-label">Total Features</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-info">
                <span className="stat-value">{featureData.top_feature}</span>
                <span className="stat-label">Most Used</span>
              </div>
            </div>
          </div>

          <div className="chart-card">
            <h4>Feature Usage Comparison</h4>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={featureData.features} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="feature_name" type="category" width={120} />
                <Tooltip />
                <Bar dataKey="usage_count" fill="#10b981" name="Usage" />
                <Bar dataKey="active_users" fill="#6366f1" name="Active Users" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <table className="monthly-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Description</th>
                <th>Total Usage</th>
                <th>Active Users</th>
                <th>Avg Session</th>
              </tr>
            </thead>
            <tbody>
              {featureData.features.map((feature) => (
                <tr key={feature.feature_id}>
                  <td><strong>{feature.feature_name}</strong></td>
                  <td>{feature.description}</td>
                  <td>{feature.usage_count.toLocaleString()}</td>
                  <td>{feature.active_users}</td>
                  <td>{feature.avg_session_minutes} min</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  const renderBrokers = () => (
    <div className="report-section">
      <h3>🏦 Indian Broker Report</h3>
      <p className="report-desc">Track Indian brokers (73 brokers with 82 APIs)</p>
      
      {brokerData && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📦</div>
              <div className="stat-info">
                <span className="stat-value">{brokerData.total_brokers}</span>
                <span className="stat-label">Indian Brokers</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔌</div>
              <div className="stat-info">
                <span className="stat-value">{brokerData.total_broker_apis}</span>
                <span className="stat-label">Total Broker APIs</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-info">
                <span className="stat-value">{brokerData.active_brokers}</span>
                <span className="stat-label">In Use</span>
              </div>
            </div>
          </div>

          <h4>Top Brokers</h4>
          <table className="monthly-table">
            <thead>
              <tr>
                <th>Broker</th>
                <th>Usage Count</th>
                <th>Percentage</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {brokerData.top_brokers?.map((broker: any) => (
                <tr key={broker.broker_id}>
                  <td>{broker.broker_name}</td>
                  <td>{broker.usage_count}</td>
                  <td>{broker.usage_percentage}%</td>
                  <td>
                    {broker.configured ? 
                      <span className="badge" style={{ backgroundColor: '#10b981' }}>Active</span> :
                      <span className="badge" style={{ backgroundColor: '#6b7280' }}>Available</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  const renderExchanges = () => (
    <div className="report-section">
      <h3>📈 Exchange Report</h3>
      <p className="report-desc">Indian exchanges (7) and Crypto exchanges (11)</p>
      
      {exchangeData && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">🇮🇳</div>
              <div className="stat-info">
                <span className="stat-value">{exchangeData.total_indian}</span>
                <span className="stat-label">Indian Exchanges</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">₿</div>
              <div className="stat-info">
                <span className="stat-value">{exchangeData.total_crypto}</span>
                <span className="stat-label">Crypto Exchanges</span>
              </div>
            </div>
          </div>

          <h4>Indian Exchanges</h4>
          <table className="monthly-table">
            <thead>
              <tr>
                <th>Exchange</th>
                <th>Usage</th>
                <th>Volume</th>
              </tr>
            </thead>
            <tbody>
              {exchangeData.indian_exchanges?.map((ex: any) => (
                <tr key={ex.exchange_id}>
                  <td><strong>{ex.exchange_name}</strong></td>
                  <td>{ex.usage_count}</td>
                  <td>₹{ex.volume_traded.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h4>Crypto Exchanges (11 connected)</h4>
          <table className="monthly-table">
            <thead>
              <tr>
                <th>Exchange</th>
                <th>Usage</th>
                <th>Volume</th>
              </tr>
            </thead>
            <tbody>
              {exchangeData.crypto_exchanges?.map((ex: any) => (
                <tr key={ex.exchange_id}>
                  <td><strong>{ex.exchange_name}</strong></td>
                  <td>{ex.usage_count}</td>
                  <td>${ex.volume_traded.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  const renderIndicators = () => (
    <div className="report-section">
      <h3>📊 Indicator Usage Report</h3>
      <p className="report-desc">Track which indicators are most used in strategies</p>
      
      {indicatorData && (
        <>
          <div className="stat-card inline">
            <span className="stat-value">{indicatorData.total_indicators}</span>
            <span className="stat-label">Available Indicators</span>
          </div>

          <div className="chart-card">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={indicatorData.top_indicators}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="indicator_name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="usage_count" fill="#6366f1" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <table className="monthly-table">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Usage Count</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {indicatorData.all_indicators?.map((ind: any) => (
                <tr key={ind.indicator_id}>
                  <td>{ind.indicator_name}</td>
                  <td>{ind.usage_count}</td>
                  <td>{ind.usage_percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  const renderChannels = () => (
    <div className="report-section">
      <h3>📱 Notification Channels Report</h3>
      <p className="report-desc">Track notification channel preferences</p>
      
      {channelData && (
        <>
          <div className="chart-card">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={channelData.channels}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ channel_name, percentage }) => `${channel_name}: ${percentage}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="usage_count"
                  nameKey="channel_name"
                >
                  {channelData.channels.map((_: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <table className="monthly-table">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Icon</th>
                <th>Users</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {channelData.channels?.map((ch: any) => (
                <tr key={ch.channel_id}>
                  <td>{ch.channel_name}</td>
                  <td>{ch.icon}</td>
                  <td>{ch.usage_count}</td>
                  <td>{ch.usage_percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  const renderOperators = () => (
    <div className="report-section">
      <h3>🔣 Condition Operators Report</h3>
      <p className="report-desc">Track which operators are most used in strategy conditions</p>
      
      {operatorData && (
        <>
          <div className="chart-card">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={operatorData.operators}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="operator_name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="usage_count" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <table className="monthly-table">
            <thead>
              <tr>
                <th>Operator</th>
                <th>Symbol</th>
                <th>Usage Count</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {operatorData.operators?.map((op: any) => (
                <tr key={op.operator_id}>
                  <td>{op.operator_name}</td>
                  <td><code>{op.symbol}</code></td>
                  <td>{op.usage_count}</td>
                  <td>{op.usage_percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  // B2: Render functions for new report tabs
  const renderUserActivityReport = () => (
    <div className="report-section">
      <div className="report-header">
        <h3>👤 User Activity Report</h3>
        <div className="report-filters">
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_year">This Year</option>
          </select>
          <input 
            type="text" 
            placeholder="Search user or activity..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Summary Cards */}
      {activitySummary && (
        <div className="report-summary-cards">
          <div className="summary-card">
            <span className="summary-label">Total Activities</span>
            <span className="summary-value">{activitySummary.total_activities}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Unique Users</span>
            <span className="summary-value">{activitySummary.unique_users}</span>
          </div>
          <div className="summary-card success">
            <span className="summary-label">Successful</span>
            <span className="summary-value">{activitySummary.successful_activities}</span>
          </div>
          <div className="summary-card danger">
            <span className="summary-label">Failed</span>
            <span className="summary-value">{activitySummary.failed_activities}</span>
          </div>
        </div>
      )}

      {/* Activity Table */}
      <div className="report-table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Activity</th>
              <th>Details</th>
              <th>Device</th>
              <th>IP Address</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {userActivities.filter(a => 
              !searchTerm || 
              a.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              a.activity_type.toLowerCase().includes(searchTerm.toLowerCase())
            ).map((activity) => (
              <tr key={activity.id}>
                <td>{activity.user_name}</td>
                <td><span className="badge">{activity.activity_type.replace('_', ' ')}</span></td>
                <td>{activity.details || '-'}</td>
                <td>{activity.device_type}</td>
                <td>{activity.ip_address}</td>
                <td>
                  <span className={`status ${activity.status}`}>
                    {activity.status}
                  </span>
                </td>
                <td>{new Date(activity.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Export Bar */}
      <div className="export-bar">
        <span>Export:</span>
        <button onClick={() => adminExports.auditLogs()}>📄 CSV</button>
        <button onClick={() => adminExports.auditLogs()}>📊 XLS</button>
        <button onClick={() => adminExports.auditLogs()}>📋 JSON</button>
        <button onClick={() => adminExports.auditLogs()}>📑 PDF</button>
        <button onClick={() => adminExports.auditLogs()}>🤖 AI</button>
      </div>
    </div>
  );

  const renderTradingReport = () => (
    <div className="report-section">
      <div className="report-header">
        <h3>📈 Trading Report</h3>
        <div className="report-filters">
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="today">Today</option>
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="this_month">This Month</option>
            <option value="this_year">This Year</option>
          </select>
          <input 
            type="text" 
            placeholder="Search symbol or user..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Summary Cards */}
      {tradingSummary && (
        <div className="report-summary-cards">
          <div className="summary-card">
            <span className="summary-label">Total Trades</span>
            <span className="summary-value">{tradingSummary.total_trades}</span>
          </div>
          <div className="summary-card success">
            <span className="summary-label">Total P&L</span>
            <span className="summary-value">₹{tradingSummary.total_pnl?.toLocaleString()}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Win Rate</span>
            <span className="summary-value">{tradingSummary.win_rate?.toFixed(1)}%</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Profit Factor</span>
            <span className="summary-value">{tradingSummary.profit_factor?.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Trades Table */}
      <div className="report-table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Trade ID</th>
              <th>User</th>
              <th>Strategy</th>
              <th>Symbol</th>
              <th>Exchange</th>
              <th>Side</th>
              <th>Qty</th>
              <th>Entry</th>
              <th>Exit</th>
              <th>P&L</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {trades.filter(t =>
              !searchTerm ||
              t.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
              t.user_name.toLowerCase().includes(searchTerm.toLowerCase())
            ).map((trade) => (
              <tr key={trade.id}>
                <td>{trade.trade_id}</td>
                <td>{trade.user_name}</td>
                <td>{trade.strategy_name || '-'}</td>
                <td>{trade.symbol}</td>
                <td>{trade.exchange}</td>
                <td><span className={`badge ${trade.side}`}>{trade.side}</span></td>
                <td>{trade.quantity}</td>
                <td>₹{trade.entry_price.toFixed(2)}</td>
                <td>₹{trade.exit_price?.toFixed(2) || '-'}</td>
                <td className={trade.pnl >= 0 ? 'positive' : 'negative'}>
                  {trade.pnl ? `₹${trade.pnl.toLocaleString()}` : '-'}
                </td>
                <td><span className={`status ${trade.status}`}>{trade.status}</span></td>
                <td>{new Date(trade.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Export Bar */}
      <div className="export-bar">
        <span>Export:</span>
        <button onClick={() => adminExports.transactions()}>📄 CSV</button>
        <button onClick={() => adminExports.transactions()}>📊 XLS</button>
        <button onClick={() => adminExports.transactions()}>📋 JSON</button>
        <button onClick={() => adminExports.transactions()}>📑 PDF</button>
        <button onClick={() => adminExports.transactions()}>🤖 AI</button>
      </div>
    </div>
  );

  const renderRevenueReport = () => (
    <div className="report-section">
      <div className="report-header">
        <h3>💰 Revenue Report</h3>
        <div className="report-filters">
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_quarter">This Quarter</option>
            <option value="this_year">This Year</option>
          </select>
          <input 
            type="text" 
            placeholder="Search user..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Revenue Metrics */}
      {revenueSummary && (
        <div className="report-summary-cards">
          <div className="summary-card success">
            <span className="summary-label">MRR</span>
            <span className="summary-value">₹{revenueSummary.mrr?.toLocaleString()}</span>
          </div>
          <div className="summary-card success">
            <span className="summary-label">ARR</span>
            <span className="summary-value">₹{revenueSummary.arr?.toLocaleString()}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">ARPU</span>
            <span className="summary-value">₹{revenueSummary.arpu?.toLocaleString()}</span>
          </div>
          <div className="summary-card danger">
            <span className="summary-label">Churn Rate</span>
            <span className="summary-value">{revenueSummary.churn_rate?.toFixed(1)}%</span>
          </div>
        </div>
      )}

      {/* Subscriptions Table */}
      <div className="report-table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Plan</th>
              <th>Billing</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Started</th>
              <th>Expires</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.filter(s =>
              !searchTerm ||
              s.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              s.user_email.toLowerCase().includes(searchTerm.toLowerCase())
            ).map((sub) => (
              <tr key={sub.id}>
                <td>{sub.user_name}</td>
                <td>{sub.user_email}</td>
                <td><span className="badge">{sub.plan_name}</span></td>
                <td>{sub.billing_cycle}</td>
                <td>₹{sub.amount.toLocaleString()}</td>
                <td><span className={`status ${sub.status}`}>{sub.status}</span></td>
                <td>{new Date(sub.started_at).toLocaleDateString()}</td>
                <td>{new Date(sub.expires_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Export Bar */}
      <div className="export-bar">
        <span>Export:</span>
        <button onClick={() => adminExports.subscriptionsBilling()}>📄 CSV</button>
        <button onClick={() => adminExports.subscriptionsBilling()}>📊 XLS</button>
        <button onClick={() => adminExports.subscriptionsBilling()}>📋 JSON</button>
        <button onClick={() => adminExports.subscriptionsBilling()}>📑 PDF</button>
        <button onClick={() => adminExports.subscriptionsBilling()}>🤖 AI</button>
      </div>
    </div>
  );

  const renderAuditLogsReport = () => (
    <div className="report-section">
      <div className="report-header">
        <h3>🔒 Audit Logs Report</h3>
        <div className="report-filters">
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="today">Today</option>
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="this_month">This Month</option>
          </select>
          <input 
            type="text" 
            placeholder="Search action or resource..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Audit Summary */}
      {auditSummary && (
        <div className="report-summary-cards">
          <div className="summary-card">
            <span className="summary-label">Total Events</span>
            <span className="summary-value">{auditSummary.total_events}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Unique Users</span>
            <span className="summary-value">{auditSummary.unique_users}</span>
          </div>
          <div className="summary-card danger">
            <span className="summary-label">Failed Actions</span>
            <span className="summary-value">{auditSummary.failed_events}</span>
          </div>
          <div className="summary-card warning">
            <span className="summary-label">Security Alerts</span>
            <span className="summary-value">{auditSummary.security_alerts}</span>
          </div>
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="report-table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Action</th>
              <th>Resource Type</th>
              <th>Resource ID</th>
              <th>IP Address</th>
              <th>Status</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {auditLogs.filter(l =>
              !searchTerm ||
              l.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
              l.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
            ).map((log) => (
              <tr key={log.id}>
                <td>{log.user_name || 'System'}</td>
                <td><span className="badge">{log.action}</span></td>
                <td>{log.resource_type}</td>
                <td>{log.resource_id}</td>
                <td>{log.ip_address}</td>
                <td>
                  <span className={`status ${log.status}`}>
                    {log.status}
                  </span>
                </td>
                <td>{new Date(log.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Export Bar */}
      <div className="export-bar">
        <span>Export:</span>
        <button onClick={() => adminExports.auditLogs()}>📄 CSV</button>
        <button onClick={() => adminExports.auditLogs()}>📊 XLS</button>
        <button onClick={() => adminExports.auditLogs()}>📋 JSON</button>
        <button onClick={() => adminExports.auditLogs()}>📑 PDF</button>
        <button onClick={() => adminExports.auditLogs()}>🤖 AI</button>
      </div>
    </div>
  );

  const renderReport = () => {
    switch (activeReport) {
      case 'overview': return renderOverview();
      case 'activity': return renderUserActivityReport();
      case 'trading': return renderTradingReport();
      case 'revenue': return renderRevenueReport();
      case 'audit': return renderAuditLogsReport();
      case 'themes': return renderThemes();
      case 'languages': return renderLanguages();
      case 'features': return renderFeatures();
      case 'brokers': return renderBrokers();
      case 'exchanges': return renderExchanges();
      case 'indicators': return renderIndicators();
      case 'channels': return renderChannels();
      case 'operators': return renderOperators();
      default: return renderOverview();
    }
  };

  if (loading) {
    return <div className="loading">Loading reports...</div>;
  }

  // Prepare data for export
  const allReportData = {
    overview,
    brokers: brokerData,
    exchanges: exchangeData,
    channels: channelData,
    indicators: indicatorData,
    keywords: keywordData,
    operators: operatorData,
    themes: themeData,
    languages: languageData,
    features: featureData
  };

  return (
    <div className="admin-reports">
      {/* Admin Export Section */}
      <div className="admin-export-section">
        <div className="export-header">
          <h3>📥 Export Reports</h3>
          <p>Download platform data in multiple formats</p>
        </div>
        <div className="export-buttons">
          <button 
            onClick={() => adminExports.platformReports(allReportData).csv()} 
            className="export-btn"
          >
            📄 CSV
          </button>
          <button 
            onClick={() => adminExports.platformReports(allReportData).xls()} 
            className="export-btn primary"
          >
            📊 Excel
          </button>
          <button 
            onClick={() => adminExports.platformReports(allReportData).json()} 
            className="export-btn"
          >
            📋 JSON
          </button>
          <button 
            onClick={() => adminExports.platformReports(allReportData).pdf()} 
            className="export-btn"
          >
            📑 PDF
          </button>
          <button 
            onClick={() => adminExports.platformReports(allReportData).ai()} 
            className="export-btn ai"
          >
            🤖 AI
          </button>
        </div>
      </div>

      {/* B2: Reports Navigation with new tabs */}
      <nav className="reports-nav">
        <button className={activeReport === 'overview' ? 'active' : ''} onClick={() => setActiveReport('overview')}>
          📊 Overview
        </button>
        <button className={activeReport === 'activity' ? 'active' : ''} onClick={() => setActiveReport('activity')}>
          👤 User Activity
        </button>
        <button className={activeReport === 'trading' ? 'active' : ''} onClick={() => setActiveReport('trading')}>
          📈 Trading
        </button>
        <button className={activeReport === 'revenue' ? 'active' : ''} onClick={() => setActiveReport('revenue')}>
          💰 Revenue
        </button>
        <button className={activeReport === 'audit' ? 'active' : ''} onClick={() => setActiveReport('audit')}>
          🔒 Audit Logs
        </button>
        <button className={activeReport === 'themes' ? 'active' : ''} onClick={() => setActiveReport('themes')}>
          🎨 Themes
        </button>
        <button className={activeReport === 'languages' ? 'active' : ''} onClick={() => setActiveReport('languages')}>
          🌐 Languages
        </button>
        <button className={activeReport === 'features' ? 'active' : ''} onClick={() => setActiveReport('features')}>
          🚀 Features
        </button>
        <button className={activeReport === 'brokers' ? 'active' : ''} onClick={() => setActiveReport('brokers')}>
          🏦 Brokers
        </button>
        <button className={activeReport === 'exchanges' ? 'active' : ''} onClick={() => setActiveReport('exchanges')}>
          📈 Exchanges
        </button>
        <button className={activeReport === 'indicators' ? 'active' : ''} onClick={() => setActiveReport('indicators')}>
          📊 Indicators
        </button>
        <button className={activeReport === 'channels' ? 'active' : ''} onClick={() => setActiveReport('channels')}>
          📱 Channels
        </button>
        <button className={activeReport === 'operators' ? 'active' : ''} onClick={() => setActiveReport('operators')}>
          🔣 Operators
        </button>
      </nav>

      <div className="reports-content">
        {renderReport()}
      </div>
    </div>
  );
};

export default AdminReports;
